/**
 * Stage 73.20p — Reimbursements & Advances Tracker.
 *
 * Unified tracker for finance team to:
 *   - See all reimbursement claims with their policy status
 *   - Filter by status (pending / approved / paid / deviation)
 *   - Mark claims as PAID with payment date / mode / ref / remarks
 *   - See all open advances (loans with outstanding > 0)
 *   - Disburse approved loans with payment trail
 *
 * Reads from existing endpoints; new payment endpoints are Stage 73.20p.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import BulkUploadModal from '../components/BulkUploadModal';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Receipt, IndianRupee, AlertTriangle, CheckCircle, Loader2, RefreshCw,
  CreditCard, ShieldCheck, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PAYMENT_MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'CASH'];

const STATUS_PILL = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-blue-100 text-blue-800 border-blue-200',
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  disbursed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
  active: 'bg-blue-100 text-blue-800 border-blue-200',
  closed: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function ReimbursementsAdvancesTracker() {
  const [selectedCompany] = useActiveCompany();
  const [tab, setTab] = useState('reimbursements');
  const [claims, setClaims] = useState([]);
  const [loans, setLoans] = useState([]);
  const [openAdvances, setOpenAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  // Payment modal
  const [paymentTarget, setPaymentTarget] = useState(null); // {type: 'reimbursement' | 'loan', id, label}
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    payment_mode: 'NEFT', transaction_ref: '', remarks: '',
  });
  const [paying, setPaying] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const loadAll = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const [c, l, o] = await Promise.all([
        axios.get(`${API}/payroll/reimbursements?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API}/payroll/loans?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API}/payroll/loans/_summary/open-advances?company_id=${selectedCompany}`, { withCredentials: true }),
      ]);
      setClaims(c.data || []);
      setLoans(l.data || []);
      setOpenAdvances(o.data || []);
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [selectedCompany]);

  useEffect(() => { loadAll(); }, [loadAll]);


  const submitPayment = async () => {
    if (!paymentTarget) return;
    if (!paymentForm.payment_date || !paymentForm.payment_mode) {
      toast.error('Payment date and mode are required');
      return;
    }
    setPaying(true);
    try {
      const url = paymentTarget.type === 'loan'
        ? `${API}/payroll/loans/${paymentTarget.id}/disburse`
        : `${API}/payroll/reimbursements/${paymentTarget.id}/pay`;
      await axios.post(url, paymentForm, { withCredentials: true });
      toast.success(paymentTarget.type === 'loan' ? 'Loan disbursed' : 'Reimbursement marked paid');
      setPaymentTarget(null);
      setPaymentForm({
        payment_date: new Date().toISOString().slice(0, 10),
        payment_mode: 'NEFT', transaction_ref: '', remarks: '',
      });
      await loadAll();
    } catch (e) {
      toast.error('Payment failed: ' + (e?.response?.data?.detail || e.message));
    }
    setPaying(false);
  };

  // Filter claims/loans
  const filteredClaims = claims.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'deviation') return c.needs_deviation_approval;
    return c.status === filter;
  });
  const filteredLoans = loans.filter(l => {
    if (filter === 'all') return true;
    if (filter === 'special') return l.needs_special_approval;
    return l.status === filter;
  });

  // Stats
  const stats = {
    pending_claims: claims.filter(c => c.status === 'pending').length,
    approved_claims: claims.filter(c => c.status === 'approved').length,
    deviation_claims: claims.filter(c => c.needs_deviation_approval).length,
    paid_claims: claims.filter(c => c.status === 'paid').length,
    active_loans: loans.filter(l => l.status === 'active' || l.status === 'disbursed').length,
    special_loans: loans.filter(l => l.needs_special_approval).length,
    total_outstanding: openAdvances.reduce((s, x) => s + (x.total_outstanding || 0), 0),
  };

  return (
    <MainLayout>
      <div className="space-y-6 p-6" data-testid="reimbursements-tracker-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="w-6 h-6 text-blue-600" />
              Reimbursements &amp; Advances Tracker
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Finance view — track claims, mark payments, monitor open advances.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/payroll/reimbursement-policy" data-testid="open-policy-page">
              <Button variant="outline" size="sm">
                <ShieldCheck className="w-3 h-3 mr-1" /> Policy Master
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkUpload(true)}
              data-testid="reimb-bulk-upload-btn"
            >
              <Upload className="w-3 h-3 mr-1" /> Bulk Upload
            </Button>
            <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
          </div>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <StatTile label="Pending claims" value={stats.pending_claims} color="amber" />
          <StatTile label="Approved" value={stats.approved_claims} color="blue" />
          <StatTile label="🚩 Deviation" value={stats.deviation_claims} color="rose" />
          <StatTile label="Paid" value={stats.paid_claims} color="emerald" />
          <StatTile label="Active loans" value={stats.active_loans} color="blue" />
          <StatTile label="🚩 Special appr" value={stats.special_loans} color="rose" />
          <StatTile label="Open ₹" value={`₹${(stats.total_outstanding || 0).toLocaleString('en-IN')}`} color="slate" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200" data-testid="tracker-tabs">
          {['reimbursements', 'loans', 'open_advances'].map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setFilter('all'); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              data-testid={`tab-${t}`}
            >
              {t === 'reimbursements' ? 'Reimbursements' : t === 'loans' ? 'Loans & Advances' : 'Open Advances'}
            </button>
          ))}
        </div>

        {/* Filter */}
        {tab !== 'open_advances' && (
          <div className="flex gap-2 text-xs items-center">
            <span className="text-slate-500">Filter:</span>
            {tab === 'reimbursements' ? (
              ['all', 'pending', 'approved', 'paid', 'rejected', 'deviation'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-2 py-1 rounded ${filter === f ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  data-testid={`filter-${f}`}
                >{f}</button>
              ))
            ) : (
              ['all', 'active', 'disbursed', 'closed', 'special'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-2 py-1 rounded ${filter === f ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >{f}</button>
              ))
            )}
          </div>
        )}

        {/* === REIMBURSEMENTS table === */}
        {tab === 'reimbursements' && (
          <Card className="p-3 overflow-x-auto">
            <table className="w-full text-xs" data-testid="claims-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Employee</th>
                  <th className="text-left p-2">Category</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-left p-2">Bill Date</th>
                  <th className="text-left p-2">Description</th>
                  <th className="text-left p-2">Policy</th>
                  <th className="text-left p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.length === 0 ? (
                  <tr><td colSpan={8} className="p-4 text-center text-slate-500 italic">No reimbursement claims.</td></tr>
                ) : filteredClaims.map(c => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="p-2 font-medium">{c.employee_name || c.employee_id?.slice(-6)}</td>
                    <td className="p-2">{c.head_code || c.category || '—'}</td>
                    <td className="p-2 text-right font-medium">₹{Number(c.claim_amount || c.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="p-2 text-slate-600">{c.bill_date || '—'}</td>
                    <td className="p-2 text-slate-700 max-w-xs truncate" title={c.description}>{c.description || '—'}</td>
                    <td className="p-2">
                      {c.needs_deviation_approval ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-rose-100 text-rose-700 border border-rose-200" title={c.policy_check?.policy_note}>
                          🚩 Exceeds by ₹{c.policy_check?.exceeds_by || 0}
                        </span>
                      ) : c.policy_check?.has_policy ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200">
                          ✓ Within
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">no policy</span>
                      )}
                    </td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] border ${STATUS_PILL[c.status] || 'bg-slate-100'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="p-2 text-right">
                      {c.status === 'approved' && (
                        <Button size="sm" variant="outline" onClick={() => setPaymentTarget({ type: 'reimbursement', id: c.id, label: `${c.head_code} ₹${c.claim_amount}` })} data-testid={`pay-claim-${c.id}`}>
                          <CreditCard className="w-3 h-3 mr-1" /> Pay
                        </Button>
                      )}
                      {c.status === 'paid' && c.transaction_ref && (
                        <span className="text-[10px] text-emerald-600" title={`${c.payment_mode} · ${c.payment_date}`}>✓ {c.transaction_ref.slice(0, 12)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* === LOANS table === */}
        {tab === 'loans' && (
          <Card className="p-3 overflow-x-auto">
            <table className="w-full text-xs" data-testid="loans-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Employee</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-right p-2">Principal</th>
                  <th className="text-right p-2">EMI</th>
                  <th className="text-right p-2">Outstanding</th>
                  <th className="text-left p-2">Approval</th>
                  <th className="text-left p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLoans.length === 0 ? (
                  <tr><td colSpan={8} className="p-4 text-center text-slate-500 italic">No loans / advances.</td></tr>
                ) : filteredLoans.map(l => (
                  <tr key={l.id} className="border-t border-slate-100">
                    <td className="p-2 font-medium">{l.employee_name || l.employee_id?.slice(-6)}</td>
                    <td className="p-2">{l.loan_type}</td>
                    <td className="p-2 text-right">₹{Number(l.principal_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="p-2 text-right">₹{Number(l.emi_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="p-2 text-right font-medium">₹{Number(l.outstanding_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="p-2">
                      {l.needs_special_approval ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-rose-100 text-rose-700 border border-rose-200" title={`Existing open: ${l.existing_open_advances}`}>
                          🚩 Special ({l.existing_open_advances})
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200">
                          Standard
                        </span>
                      )}
                    </td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] border ${STATUS_PILL[l.status] || 'bg-slate-100'}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="p-2 text-right">
                      {(l.status === 'active' || l.status === 'approved') && (
                        <Button size="sm" variant="outline" onClick={() => setPaymentTarget({ type: 'loan', id: l.id, label: `${l.loan_type} ₹${l.principal_amount}` })} data-testid={`disburse-loan-${l.id}`}>
                          <CreditCard className="w-3 h-3 mr-1" /> Disburse
                        </Button>
                      )}
                      {l.status === 'disbursed' && l.transaction_ref && (
                        <span className="text-[10px] text-emerald-600" title={`${l.payment_mode} · ${l.payment_date}`}>✓ {l.transaction_ref.slice(0, 12)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* === OPEN ADVANCES summary === */}
        {tab === 'open_advances' && (
          <Card className="p-3 overflow-x-auto">
            <p className="text-xs text-slate-600 mb-2">
              Employees with one or more <b>open advances</b> (status = active / disbursed and outstanding {'>'} 0).
              Any new advance request for these employees will require <b>special approval</b>.
            </p>
            <table className="w-full text-xs" data-testid="open-advances-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Employee</th>
                  <th className="text-left p-2">Code</th>
                  <th className="text-right p-2">Open Loans</th>
                  <th className="text-right p-2">Total Outstanding</th>
                  <th className="text-left p-2">Advances</th>
                </tr>
              </thead>
              <tbody>
                {openAdvances.length === 0 ? (
                  <tr><td colSpan={5} className="p-4 text-center text-slate-500 italic">No open advances. Clean slate ✓</td></tr>
                ) : openAdvances.map(o => (
                  <tr key={o.employee_id} className="border-t border-slate-100">
                    <td className="p-2 font-medium">{o.employee_name || '—'}</td>
                    <td className="p-2 text-slate-600">{o.employee_code || '—'}</td>
                    <td className="p-2 text-right font-bold text-rose-600">{o.loan_count}</td>
                    <td className="p-2 text-right font-medium">₹{Number(o.total_outstanding || 0).toLocaleString('en-IN')}</td>
                    <td className="p-2 text-slate-700">
                      {(o.loans || []).map(ln => (
                        <span key={ln.id} className="inline-block mr-1.5 text-[10px] px-1 py-0.5 bg-slate-100 rounded">
                          {ln.loan_type}: ₹{Number(ln.outstanding).toLocaleString('en-IN')}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Payment modal */}
        {paymentTarget && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPaymentTarget(null)}>
            <div className="bg-white rounded-lg p-5 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="payment-modal">
              <h3 className="font-semibold text-slate-900 mb-1">
                Record Payment: {paymentTarget.type === 'loan' ? 'Loan Disbursement' : 'Reimbursement Payment'}
              </h3>
              <p className="text-xs text-slate-600 mb-3">{paymentTarget.label}</p>
              <div className="space-y-3 text-xs">
                <label className="block">
                  <span className="text-slate-700 font-medium">Payment Date *</span>
                  <input type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5" />
                </label>
                <label className="block">
                  <span className="text-slate-700 font-medium">Payment Mode *</span>
                  <select value={paymentForm.payment_mode} onChange={e => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5">
                    {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-slate-700 font-medium">Transaction Ref</span>
                  <input value={paymentForm.transaction_ref} onChange={e => setPaymentForm({ ...paymentForm, transaction_ref: e.target.value })}
                    placeholder="UTR / cheque no / UPI ref" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5" />
                </label>
                <label className="block">
                  <span className="text-slate-700 font-medium">Remarks</span>
                  <textarea value={paymentForm.remarks} onChange={e => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                    rows={2} className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5" />
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setPaymentTarget(null)}>Cancel</Button>
                <Button size="sm" onClick={submitPayment} disabled={paying} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payment-confirm-btn">
                  {paying ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />} Confirm Payment
                </Button>
              </div>
            </div>
          </div>
        )}

        <BulkUploadModal
          open={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          title="Bulk Reimbursement Claims Upload"
          description="Upload a CSV to create multiple reimbursement claims at once. Policy gate runs per row — out-of-cap claims are flagged for special approval."
          templateUrl="/payroll/reimbursements/bulk-upload/template"
          uploadUrl="/payroll/reimbursements/bulk-upload"
          uploadParams={{ company_id: selectedCompany }}
          templateFileName="reimbursements_bulk_upload_template.csv"
          onUploadSuccess={() => loadAll()}
          columns={['employee_code', 'head_code', 'claim_amount', 'bill_date', 'description', 'subcategory']}
        />
      </div>
    </MainLayout>
  );
}

function StatTile({ label, value, color }) {
  const colors = {
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
  };
  return (
    <div className={`rounded p-2 border text-center ${colors[color] || 'bg-slate-50'}`}>
      <div className="text-[10px] uppercase opacity-70">{label}</div>
      <div className="text-base font-bold mt-0.5">{value}</div>
    </div>
  );
}
